import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { isSuper, toIELocal, fromIELocal } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil } from "lucide-react";

let schemaCache = null;
const useSchema = (enabled) => {
  const [s, setS] = useState(schemaCache);
  useEffect(() => { if (enabled && !schemaCache) api.get("/admin/schema").then((r) => { schemaCache = r.data; setS(r.data); }).catch(() => {}); }, [enabled]);
  return s;
};
const pretty = (k) => k.replace(/_/g, " ").replace(/\bat\b/, "").replace(/^\w/, (c) => c.toUpperCase());
const toLocal = toIELocal;
const breakMins = (r) => Math.round((r.breaks || []).filter((b) => b.end).reduce((a, b) => a + (new Date(b.end) - new Date(b.start)) / 60000, 0));

function Field({ k, t, v, set }) {
  if (Array.isArray(t)) return (
    <Select value={v || ""} onValueChange={set}><SelectTrigger data-testid={`super-edit-${k}`}><SelectValue /></SelectTrigger>
      <SelectContent>{t.map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
  );
  if (t === "bool") return <input type="checkbox" checked={!!v} onChange={(e) => set(e.target.checked)} data-testid={`super-edit-${k}`} className="w-5 h-5 accent-orange-500" />;
  const type = t === "number" ? "number" : t === "date" ? "date" : t === "datetime" ? "datetime-local" : "text";
  return <Input type={type} step="any" value={v ?? ""} onChange={(e) => set(e.target.value)} data-testid={`super-edit-${k}`} />;
}

export function SuperEdit({ entity, record, onDone, label }) {
  const { user } = useAuth();
  const ok = isSuper(user?.role) && !user?.impersonated_by;
  const schema = useSchema(ok);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const fields = schema?.[entity] || {};
  const start = () => {
    const init = {};
    Object.entries(fields).forEach(([k, t]) => { init[k] = k === "break_minutes" ? breakMins(record) : t === "datetime" ? toLocal(record[k]) : record[k]; });
    setF(init); setOpen(true);
  };
  if (!ok || !schema) return null;
  const save = async () => {
    const body = { ...f };
    Object.entries(fields).forEach(([k, t]) => { if (t === "datetime" && body[k]) body[k] = body[k] === toLocal(record[k]) ? record[k] : fromIELocal(body[k]); });
    try { await api.patch(`/admin/${entity}/${record.id}`, body); toast.success("Saved"); setOpen(false); onDone?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <>
      <button onClick={(e) => { e.stopPropagation(); start(); }} title={`Edit ${label || entity}`} data-testid={`super-edit-${entity}-${record.id}`}
        className="inline-flex items-center justify-center h-8 px-2 rounded-md border border-sky-500/40 text-sky-400 hover:bg-sky-500/10 transition-colors"><Pencil className="w-4 h-4" /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <DialogHeader><DialogTitle className="font-heading uppercase">Edit {label || entity.replace(/_/g, " ")}</DialogTitle>
            <DialogDescription>Super User edit. Changes are recorded in the audit trail.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {Object.entries(fields).map(([k, t]) => (
              <div key={k} className={t === "bool" ? "flex items-center gap-3" : ""}><Label className="text-xs">{pretty(k)}{t === "datetime" ? " (Irish time)" : ""}</Label>
                <div className={t === "bool" ? "" : "mt-1"}><Field k={k} t={t} v={f[k]} set={(v) => setF({ ...f, [k]: v })} /></div></div>
            ))}
          </div>
          <DialogFooter><Button onClick={save} data-testid="super-edit-save" className="w-full font-bold uppercase">Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
